import { useRef, useState } from "react";
import { useNavigate } from "react-router";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

import { VoltPageHeader } from "../ui/VoltPageHeader";
import { draftPath } from "./draftText";
import { WorkshopError } from "./WorkshopError";
import { WorkshopFrame, type WorkshopMeta } from "./WorkshopFrame";
import { errorText, readFileBase64, workshopApi } from "./workshopApi";

/** "automatisch" is the default: the Werkstatt picks Beschluss or Anfrage by § 19 and § 24 BezVG. */
const DRAFT_KINDS: [string, string][] = [
  ["automatisch", "Passende Form vorschlagen lassen"],
  ["Antrag", "Antrag – das Bezirksamt soll handeln (§ 19 BezVG)"],
  [
    "Empfehlung § 27",
    "Empfehlung § 27 – an eine Fachbehörde, Antwort in 6 Wochen",
  ],
  [
    "Kleine Anfrage",
    "Kleine Anfrage § 24 – ans Bezirksamt, Antwort in 8 Arbeitstagen",
  ],
  [
    "Große Anfrage",
    "Große Anfrage § 24 – ans Bezirksamt, Antwort in einem Monat",
  ],
  ["Anfrage § 27", "Anfrage § 27 – an eine Fachbehörde, Antwort in 6 Wochen"],
];
const MAX_FILES = 8;
const MAX_MB = 20;
const ACCEPT = ".pdf,.png,.jpg,.jpeg,.gif,.webp,.txt,.md,.csv";

export const WorkshopNewPage = () => (
  <WorkshopFrame>{(meta) => <NewDraftForm meta={meta} />}</WorkshopFrame>
);

const NewDraftForm = ({
  meta: { status, committees },
}: {
  meta: WorkshopMeta;
}) => {
  const navigate = useNavigate();
  const [topic, setTopic] = useState("");
  const [kind, setKind] = useState(DRAFT_KINDS[0][0]);
  const [committee, setCommittee] = useState("");
  const [notes, setNotes] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fileNotice, setFileNotice] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const locked = !status?.konfiguriert || status.verbleibend === 0;
  const totalMb = files.reduce((s, f) => s + f.size, 0) / 1024 / 1024;
  const ready = topic.trim().length >= 5 && !!committee;

  const addFiles = (added: FileList | null) => {
    if (!added) return;
    setError(null);
    setFileNotice(null);
    const offered = [...files, ...Array.from(added)];
    const all = offered.slice(0, MAX_FILES);
    if (all.reduce((s, f) => s + f.size, 0) / 1024 / 1024 > MAX_MB) {
      setError(`Die Dateien sind zusammen größer als ${MAX_MB} MB.`);
      return;
    }
    setFiles(all);
    if (offered.length > MAX_FILES) {
      const left = offered.slice(MAX_FILES).map((f) => f.name);
      setFileNotice(
        `Höchstens ${MAX_FILES} Dateien. Nicht angehängt: ${left.join(", ")}.`,
      );
    }
  };

  const submit = async () => {
    if (!ready || busy || locked) return;
    setBusy(true);
    setError(null);
    try {
      const anhaenge = await Promise.all(files.map(readFileBase64));
      const draft = await workshopApi.create({
        thema: topic.trim(),
        art: kind,
        gremium: committee,
        hinweise: notes.trim() || null,
        anhaenge,
      });
      navigate(draftPath(draft.id));
    } catch (e) {
      setError(errorText(e));
      setBusy(false);
    }
  };

  return (
    <>
      <VoltPageHeader
        title="Neuer Entwurf"
        description="Beschreibe das Thema in eigenen Worten und häng an, was du hast. Die Recherche in den Drucksachen und im Netz übernimmt die Werkstatt."
      />
      <Card>
        <CardContent className="flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <Label htmlFor="workshop-topic">Worum geht es?</Label>
            <Textarea
              id="workshop-topic"
              rows={4}
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              placeholder="Zum Beispiel: An einer großen Kreuzung im Stadtteil fehlt eine sichere Querung für Kinder auf dem Schulweg. Anwohner*innen haben sich mehrfach bei uns gemeldet."
            />
            <p className="text-xs text-muted-foreground">
              Je konkreter Ort, Anlass und Ziel, desto brauchbarer der Entwurf.
              Ganze Sätze sind besser als Stichworte.
            </p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex min-w-0 flex-col gap-2">
              <Label htmlFor="workshop-kind">Form</Label>
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger id="workshop-kind" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DRAFT_KINDS.map(([value, text]) => (
                    <SelectItem key={value} value={value}>
                      {text}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Im Zweifel vorschlagen lassen: Für Landesthemen (ÖPNV, Schulen,
                Landesstraßen) wird daraus ein § 27-Papier an die Fachbehörde,
                für fehlende Zahlen eine Anfrage.
              </p>
            </div>

            <div className="flex min-w-0 flex-col gap-2">
              <Label htmlFor="workshop-committee">
                Gremium <span className="text-destructive">*</span>
              </Label>
              <Select value={committee} onValueChange={setCommittee}>
                <SelectTrigger id="workshop-committee" className="w-full">
                  <SelectValue placeholder="Bitte wählen …" />
                </SelectTrigger>
                <SelectContent>
                  {committees.map((c) => (
                    <SelectItem key={c.gremium} value={c.gremium}>
                      {c.gremium}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Entscheidet, wer mitarbeiten kann: Mitglieder, Stellvertretung
                und Buddy dieses Ausschusses sehen und bearbeiten den Entwurf.
                Passt ein anderes besser, sagt die Bewertung das nach dem Lauf.
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="workshop-notes">Hinweise (optional)</Label>
            <Textarea
              id="workshop-notes"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Was unbedingt rein soll, was auf keinen Fall, mögliche Mitantragstellende, Sitzungstermin …"
            />
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">Unterlagen</span>
            <button
              type="button"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                addFiles(e.dataTransfer.files);
              }}
              onClick={() => fileInput.current?.click()}
              className={cn(
                "rounded-lg border-2 border-dashed px-4 py-6 text-center transition-colors",
                dragOver
                  ? "border-primary bg-primary-soft"
                  : "border-border hover:border-primary/50",
              )}
            >
              <span className="block text-sm text-muted-foreground">
                PDFs, Fotos oder Textdateien hierher ziehen oder klicken zum
                Auswählen
              </span>
              <span className="mt-1 block text-xs text-muted-foreground">
                Bis zu {MAX_FILES} Dateien, zusammen {MAX_MB} MB.
                Anwohnerschreiben, Amtsantworten, Protokolle, Studien.
              </span>
            </button>
            <input
              ref={fileInput}
              type="file"
              multiple
              accept={ACCEPT}
              className="hidden"
              data-testid="workshop-files"
              onChange={(e) => {
                addFiles(e.target.files);
                // Lets the same file be picked again after removing it.
                e.target.value = "";
              }}
            />
            {files.length > 0 && (
              <ul className="flex flex-col gap-1">
                {files.map((f, i) => (
                  <li
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-md bg-muted px-3 py-2 text-sm"
                  >
                    <span className="truncate">{f.name}</span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-xs tabular-nums text-muted-foreground">
                        {(f.size / 1024 / 1024).toFixed(1)} MB
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setFiles(files.filter((_, j) => j !== i));
                          setFileNotice(null);
                        }}
                      >
                        entfernen
                      </Button>
                    </span>
                  </li>
                ))}
                <li className="text-right text-xs tabular-nums text-muted-foreground">
                  zusammen {totalMb.toFixed(1)} MB
                </li>
              </ul>
            )}
            {fileNotice && (
              <p
                role="status"
                className="rounded-md bg-warning-soft px-3 py-2 text-sm text-warning-text"
              >
                {fileNotice}
              </p>
            )}
          </div>

          {error && <WorkshopError>{error}</WorkshopError>}

          <div className="flex flex-wrap items-center gap-3">
            <Button
              type="button"
              onClick={submit}
              disabled={busy || locked || !ready}
            >
              {busy ? "Wird gestartet …" : "Entwurf erstellen"}
            </Button>
            <span className="text-xs text-muted-foreground">
              Dauert je nach Thema drei bis acht Minuten. Du kannst die Seite
              offen lassen.
            </span>
          </div>
        </CardContent>
      </Card>
    </>
  );
};
